import { useEffect, useState } from 'react';
import { Routes, Route, useParams, Outlet, useNavigate, useLocation } from 'react-router-dom';
import portalService from '../services/portalService';
import PortalPaymentResult from './base/PortalPaymentResult';

// Import base portal components (default)
import BasePortalLayout from './base/PortalLayout';
import BasePortalHome from './base/PortalHome';
import BasePortalInvoiceList from './base/PortalInvoiceList';
import BasePortalInvoiceHistory from './base/PortalInvoiceHistory';
import BasePortalInvoiceDetails from './base/PortalInvoiceDetails';
import BasePortalPayment from './base/PortalPayment';
import BasePortalEstimateDetails from './base/PortalEstimateDetails';

// Industry component mapping
const industryComponents = {
  default: {
    Layout: BasePortalLayout,
    Home: BasePortalHome,
    InvoiceList: BasePortalInvoiceList,
    InvoiceHistory: BasePortalInvoiceHistory,
    InvoiceDetails: BasePortalInvoiceDetails,
    Payment: BasePortalPayment
  }
};

// Portal wrapper that handles validation and data loading
function PortalWrapper() {
  const { publicId, token, shortCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [error, setError] = useState(null);

  // Determine the URL base path for redirects (short URL vs legacy token vs publicId)
  const basePath = shortCode ? `/p/${shortCode}` : token ? `/t/${token}` : `/portal/${publicId}`;

  useEffect(() => {
    const validatePortal = async () => {
      // Skip validation if we already have portal data
      if (portalData) {
        return;
      }

      if (!publicId && !token && !shortCode) {
        setError('Invalid portal URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Validating portal access...', { shortCode, token, publicId });

        // Resolve: short code → backend resolve, token → validate, publicId → validate portal
        const data = shortCode
          ? await portalService.resolveShortCode(shortCode)
          : token
            ? await portalService.validateToken(token)
            : await portalService.validatePortal(publicId);

        console.log('Portal validation response:', data);
        setPortalData(data);

        // Only redirect on the exact root path for invoice tokens AND when there's no navigation state
        // This prevents interference with user-initiated navigation to pay different invoices
        const hasNavigationState = location.state?.selectedInvoices || location.state?.fromUserAction;

        // Auto-redirect for estimate tokens → estimate detail page
        if (data.tokenData?.type === 'estimate' && location.pathname === basePath && !hasNavigationState) {
          if (data.tokenData?.estimateNumber) {
            console.log('Redirecting to estimate details for estimate token');
            navigate(`${basePath}/estimates/${data.tokenData.estimateNumber}`, { replace: true });
          }
        }

        if (data.tokenData?.type === 'invoice' && location.pathname === basePath && !hasNavigationState) {
          if (data.tokenData?.invoiceStatus !== 'paid') {
            console.log('Redirecting to payment page for unpaid invoice');
            navigate(`${basePath}/pay`, { replace: true });
          } else if (data.tokenData?.invoiceNumber) {
            console.log('Redirecting to invoice details for paid invoice');
            navigate(`${basePath}/invoices/${data.tokenData.invoiceNumber}`, { replace: true });
          }
        }

      } catch (err) {
        console.error('Portal validation error:', err);
        setError(err.response?.data?.error || 'Portal not found');
      } finally {
        setLoading(false);
      }
    };

    validatePortal();
  }, [publicId, token, shortCode, portalData, basePath, location.pathname, location.state?.selectedInvoices, location.state?.fromUserAction, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Get components for the customer's industry
  const components = industryComponents[portalData?.industry] || industryComponents.default;
  const Layout = components.Layout;

  // Create context data that child components expect
  const contextData = {
    portalData,
    customerInfo: {
      customer: {
        publicId: portalData.customerPublicId,
        name: portalData.customerName,
        email: portalData.customerEmail || '',
        merchantName: portalData.merchantName
      },
      summary: {},
      industryType: portalData.industryType
    },
    components,
    token: shortCode || token || publicId,
    tokenData: portalData.tokenData,
    customerPublicId: portalData.customerPublicId
  };

// Don't render payment component directly - let the router handle navigation
// The router will show the appropriate component based on the route

// For customer tokens or no token, show normal portal
return (
  <Layout portalData={portalData}>
    <Outlet context={contextData} />
  </Layout>
);
}

// Main portal router
function PortalRouter() {
  console.log('🚀 PortalRouter rendered, current path:', window.location.pathname);
  console.log('Current location:', window.location.href);
  console.log('Routes are mounting...');
  return (
    <Routes>
      {/* Token-based route */}
      <Route path="/*" element={<PortalWrapper />}>
        <Route index element={<BasePortalHome />} />
        <Route path="invoices" element={<BasePortalInvoiceList />} />
        <Route path="pay" element={<BasePortalPayment />} />  {/* Move this BEFORE the dynamic route */}
        <Route path="payment-history" element={<BasePortalInvoiceHistory />} />
        <Route path="payment-result" element={<PortalPaymentResult />} />
        <Route path="estimates/:estimateNumber" element={<BasePortalEstimateDetails />} />
        <Route path="invoices/:invoiceNumber" element={<BasePortalInvoiceDetails />} />  {/* This should be LAST */}
      </Route>
    </Routes>
  );
}

export default PortalRouter;
